import React, { useState, useRef, useEffect } from 'react';
import { 
  MapPin, Video, GripVertical, Paperclip, UsersRound
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { cn } from '../../lib/utils';
import { EVENT_TYPES, RSVP_STATUS, WORK_HOURS, HOUR_HEIGHT, MIN_EVENT_HEIGHT, getUserColor } from './calendarConfig';

// ============================================================================
// CURRENT TIME INDICATOR
// ============================================================================
export const CurrentTimeIndicator = () => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const hours = now.getHours();
  const minutes = now.getMinutes();
  const top = (hours * HOUR_HEIGHT) + (minutes / 60 * HOUR_HEIGHT);

  return (
    <div 
      className="absolute left-0 right-0 z-20 pointer-events-none"
      style={{ top: `${top}px` }}
    >
      <div className="flex items-center">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1" />
        <div className="flex-1 h-0.5 bg-red-500" />
      </div>
    </div>
  );
};

// ============================================================================
// RESIZABLE EVENT CARD COMPONENT
// ============================================================================
const ResizableEventCard = ({ event, onClick, onDragStart, onResize, style, compact = false, canResize = true }) => {
  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
  const Icon = eventType.icon;
  const isAICreated = event.created_by_ai;
  const [isResizing, setIsResizing] = useState(false);
  const cardRef = useRef(null);

  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    const initialHeight = parseFloat(style.height) || 60;
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - e.clientY;
      const newHeight = Math.max(MIN_EVENT_HEIGHT, initialHeight + deltaY);
      if (cardRef.current) {
        cardRef.current.style.height = `${newHeight}px`;
      }
    };
    
    const handleMouseUp = (upEvent) => {
      setIsResizing(false);
      const deltaY = upEvent.clientY - e.clientY;
      const newHeight = Math.max(MIN_EVENT_HEIGHT, initialHeight + deltaY);
      const newDurationHours = newHeight / HOUR_HEIGHT;
      
      const startDate = new Date(event.start_date);
      const newEndDate = new Date(startDate.getTime() + newDurationHours * 60 * 60 * 1000);
      
      if (onResize) {
        onResize(event, newEndDate);
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const hasRsvp = event.rsvp_required && event.user_rsvp;
  const rsvpStatus = hasRsvp ? RSVP_STATUS[event.user_rsvp] : null;
  const hasAttachments = event.attachments && event.attachments.length > 0;
  
  return (
    <div
      ref={cardRef}
      draggable={!isResizing}
      onDragStart={(e) => !isResizing && onDragStart(e, event)}
      onClick={() => !isResizing && onClick(event)}
      className={cn(
        "absolute rounded-md px-2 py-1 cursor-pointer overflow-hidden transition-all group",
        "hover:ring-2 hover:ring-offset-1 hover:ring-primary/50",
        "shadow-sm border-l-4",
        isResizing && "ring-2 ring-primary"
      )}
      style={{
        ...style,
        backgroundColor: eventType.lightColor,
        borderLeftColor: eventType.color,
      }}
    >
      <div className="flex items-start gap-1 h-full">
        {!compact && <Icon className="h-3 w-3 mt-0.5 flex-shrink-0" style={{ color: eventType.color }} />}
        <div className="flex-1 min-w-0">
          <div className={cn("font-medium truncate", compact ? "text-[10px]" : "text-xs")} style={{ color: eventType.color }}>
            {event.title}
          </div>
          {!compact && event.location && (
            <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
              <MapPin className="h-2.5 w-2.5" />
              {event.location}
            </div>
          )}
          {!compact && event.video_link && (
            <div className="text-[10px] text-blue-600 truncate flex items-center gap-1">
              <Video className="h-2.5 w-2.5" />
              Join
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5">
          {isAICreated && (
            <Badge variant="outline" className="text-[8px] px-1 py-0 h-4 bg-teal-50 text-teal-700 border-teal-200">
              AI
            </Badge>
          )}
          {hasAttachments && (
            <Paperclip className="h-3 w-3 text-muted-foreground" />
          )}
          {rsvpStatus && (
            <rsvpStatus.icon className={cn("h-3 w-3", rsvpStatus.color)} />
          )}
        </div>
      </div>
      
      {canResize && !compact && (
        <div
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-center justify-center bg-gradient-to-t from-black/10 to-transparent"
          onMouseDown={handleResizeStart}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground rotate-90" />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DAY VIEW COMPONENT
// ============================================================================
export const DayView = ({ date, events, onEventClick, onDragStart, onTimeSlotClick, onDropEvent, onEventResize }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dateStr = date.toISOString().split('T')[0];
  const isToday = dateStr === new Date().toISOString().split('T')[0];

  const dayEvents = events.filter(e => {
    const start = new Date(e.start_date).toISOString().split('T')[0];
    const end = new Date(e.end_date).toISOString().split('T')[0];
    return start === dateStr || (start <= dateStr && end >= dateStr);
  });

  const getEventStyle = (event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const duration = endHour - startHour;

    return {
      top: `${startHour * HOUR_HEIGHT}px`,
      height: `${Math.max(duration * HOUR_HEIGHT, 24)}px`,
      left: '4px',
      right: '4px',
    };
  };

  const handleDrop = (e, hour) => {
    e.preventDefault();
    const dropDate = new Date(date);
    dropDate.setHours(hour, 0, 0, 0);
    onDropEvent(e, dropDate);
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className={cn(
        "flex-shrink-0 text-center py-3 border-b w-full",
        isToday && "bg-primary/5"
      )}>
        <div className="text-xs text-muted-foreground">
          {date.toLocaleDateString('en-US', { weekday: 'short' })}
        </div>
        <div className={cn(
          "text-2xl font-semibold",
          isToday && "text-primary"
        )}>
          {date.getDate()}
        </div>
      </div>

      <ScrollArea className="flex-1 w-full">
        <div className="relative w-full" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
          {hours.map(hour => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-t border-border/50 hover:bg-accent/30 transition-colors cursor-pointer"
              style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, hour)}
              onClick={() => {
                const clickDate = new Date(date);
                clickDate.setHours(hour, 0, 0, 0);
                onTimeSlotClick(clickDate);
              }}
            >
              <span className="absolute -left-14 top-0 text-xs text-muted-foreground w-12 text-right">
                {hour.toString().padStart(2, '0')}:00
              </span>
            </div>
          ))}

          {isToday && <CurrentTimeIndicator />}

          {dayEvents.map(event => (
            <ResizableEventCard
              key={event.id}
              event={event}
              onClick={onEventClick}
              onDragStart={onDragStart}
              onResize={onEventResize}
              style={getEventStyle(event)}
              canResize={true}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// WEEK VIEW COMPONENT (Full Week: Sun-Sat)
// ============================================================================
export const WeekView = ({ startDate, events, onEventClick, onDragStart, onTimeSlotClick, onDropEvent, onEventResize }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const weekStart = new Date(startDate);
  const day = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - day);
  
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });

  const todayStr = new Date().toISOString().split('T')[0];

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const start = new Date(e.start_date).toISOString().split('T')[0];
      return start === dateStr;
    });
  };

  const getEventStyle = (event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const duration = Math.max(endHour - startHour, 0.5);

    return {
      top: `${startHour * HOUR_HEIGHT}px`,
      height: `${duration * HOUR_HEIGHT}px`,
      left: '2px',
      right: '2px',
    };
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="flex-shrink-0 grid grid-cols-8 border-b w-full">
        <div className="w-16" />
        {weekDays.map((date, i) => {
          const dateStr = date.toISOString().split('T')[0];
          const isToday = dateStr === todayStr;
          return (
            <div
              key={i}
              className={cn(
                "text-center py-2 border-l",
                isToday && "bg-primary/5"
              )}
            >
              <div className="text-xs text-muted-foreground">
                {date.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className={cn(
                "text-lg font-semibold",
                isToday && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
              )}>
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      <ScrollArea className="flex-1 w-full">
        <div className="relative grid grid-cols-8 w-full" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
          <div className="w-16">
            {hours.map(hour => (
              <div
                key={hour}
                className="absolute text-xs text-muted-foreground text-right pr-2"
                style={{ top: `${hour * HOUR_HEIGHT}px`, width: '64px' }}
              >
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {weekDays.map((date, dayIndex) => {
            const dateStr = date.toISOString().split('T')[0];
            const isToday = dateStr === todayStr;
            const dayEvents = getEventsForDay(date);

            return (
              <div key={dayIndex} className="relative border-l">
                {hours.map(hour => (
                  <div
                    key={hour}
                    className="absolute left-0 right-0 border-t border-border/30 hover:bg-accent/20 transition-colors cursor-pointer"
                    style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dropDate = new Date(date);
                      dropDate.setHours(hour, 0, 0, 0);
                      onDropEvent(e, dropDate);
                    }}
                    onClick={() => {
                      const clickDate = new Date(date);
                      clickDate.setHours(hour, 0, 0, 0);
                      onTimeSlotClick(clickDate);
                    }}
                  />
                ))}

                {isToday && <CurrentTimeIndicator />}

                {dayEvents.map(event => (
                  <ResizableEventCard
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                    onDragStart={onDragStart}
                    onResize={onEventResize}
                    style={getEventStyle(event)}
                    compact
                    canResize={true}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// WORK WEEK VIEW COMPONENT (Mon-Fri) - Full 24 hours
// ============================================================================
export const WorkWeekView = ({ startDate, events, onEventClick, onDragStart, onTimeSlotClick, onDropEvent, onEventResize }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const weekStart = new Date(startDate);
  const day = weekStart.getDay();
  const diff = day === 0 ? 1 : (day === 6 ? 2 : 1 - day);
  weekStart.setDate(weekStart.getDate() + diff);
  
  const workDays = Array.from({ length: 5 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });

  const todayStr = new Date().toISOString().split('T')[0];

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const start = new Date(e.start_date).toISOString().split('T')[0];
      return start === dateStr;
    });
  };

  const getEventStyle = (event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const duration = Math.max(endHour - startHour, 0.5);

    return {
      top: `${startHour * HOUR_HEIGHT}px`,
      height: `${duration * HOUR_HEIGHT}px`,
      left: '2px',
      right: '2px',
    };
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="flex-shrink-0 grid border-b w-full" style={{ gridTemplateColumns: '64px repeat(5, 1fr)' }}>
        <div className="w-16 border-r" />
        {workDays.map((date, i) => {
          const dateStr = date.toISOString().split('T')[0];
          const isToday = dateStr === todayStr;
          return (
            <div
              key={i}
              className={cn(
                "text-center py-2 border-l",
                isToday && "bg-primary/5"
              )}
            >
              <div className="text-xs text-muted-foreground">
                {date.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className={cn(
                "text-lg font-semibold",
                isToday && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
              )}>
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      <ScrollArea className="flex-1 w-full">
        <div className="relative grid w-full" style={{ gridTemplateColumns: '64px repeat(5, 1fr)', height: `${24 * HOUR_HEIGHT}px` }}>
          <div className="relative border-r">
            {hours.map((hour) => (
              <div
                key={hour}
                className="absolute text-xs text-muted-foreground text-right pr-2"
                style={{ top: `${hour * HOUR_HEIGHT}px`, width: '64px' }}
              >
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {workDays.map((date, dayIndex) => {
            const dateStr = date.toISOString().split('T')[0];
            const isToday = dateStr === todayStr;
            const dayEvents = getEventsForDay(date);

            return (
              <div key={dayIndex} className="relative border-l">
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className={cn(
                      "absolute left-0 right-0 border-t hover:bg-accent/20 transition-colors cursor-pointer",
                      hour >= WORK_HOURS.start && hour < WORK_HOURS.end ? "border-border/50" : "border-border/20 bg-muted/30"
                    )}
                    style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dropDate = new Date(date);
                      dropDate.setHours(hour, 0, 0, 0);
                      onDropEvent(e, dropDate);
                    }}
                    onClick={() => {
                      const clickDate = new Date(date);
                      clickDate.setHours(hour, 0, 0, 0);
                      onTimeSlotClick(clickDate);
                    }}
                  />
                ))}

                {isToday && <CurrentTimeIndicator />}

                {dayEvents.map(event => (
                  <ResizableEventCard
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                    onDragStart={onDragStart}
                    onResize={onEventResize}
                    style={getEventStyle(event)}
                    compact
                    canResize={true}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// MULTI-USER SCHEDULE VIEW
// ============================================================================
export const MultiUserScheduleView = ({ 
  date, 
  events, 
  selectedUsers, 
  allUsers,
  onEventClick, 
  onDragStart, 
  onTimeSlotClick, 
  onDropEvent,
  onEventResize 
}) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const todayStr = new Date().toISOString().split('T')[0];
  const dateStr = date.toISOString().split('T')[0];
  const isToday = dateStr === todayStr;

  const usersToShow = selectedUsers.length > 0 
    ? allUsers.filter(u => selectedUsers.includes(u.id))
    : allUsers.slice(0, 5);

  const getEventsForUser = (userId) => {
    return events.filter(e => {
      const eventDateStr = new Date(e.start_date).toISOString().split('T')[0];
      if (eventDateStr !== dateStr) return false;
      return e.created_by === userId || (e.attendees && e.attendees.includes(userId));
    });
  };

  const getEventStyle = (event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const duration = Math.max(endHour - startHour, 0.5);

    return {
      top: `${startHour * HOUR_HEIGHT}px`,
      height: `${duration * HOUR_HEIGHT}px`,
      left: '2px',
      right: '2px',
    };
  };

  if (usersToShow.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <UsersRound className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>Select people from the sidebar to view their schedules</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className={cn(
        "flex-shrink-0 text-center py-2 border-b w-full",
        isToday && "bg-primary/5"
      )}>
        <div className="text-lg font-semibold">
          {date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      <div className="flex-shrink-0 grid border-b w-full" style={{ gridTemplateColumns: `64px repeat(${usersToShow.length}, 1fr)` }}>
        <div className="w-16 border-r" />
        {usersToShow.map((user) => {
          const userColor = getUserColor(user.id);
          return (
            <div
              key={user.id}
              className="text-center py-2 border-r last:border-r-0"
              style={{ borderBottomColor: userColor.color, borderBottomWidth: '3px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={user.profile_picture} />
                  <AvatarFallback 
                    className="text-[10px]"
                    style={{ backgroundColor: userColor.lightColor, color: userColor.color }}
                  >
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium truncate max-w-[100px]">
                  {user.first_name}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <ScrollArea className="flex-1 w-full">
        <div className="relative grid w-full" style={{ gridTemplateColumns: `64px repeat(${usersToShow.length}, 1fr)`, height: `${24 * HOUR_HEIGHT}px` }}>
          <div className="relative border-r">
            {hours.map((hour) => (
              <div
                key={hour}
                className="absolute text-xs text-muted-foreground text-right pr-2"
                style={{ top: `${hour * HOUR_HEIGHT}px`, width: '64px' }}
              >
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {usersToShow.map((user) => {
            const userColor = getUserColor(user.id);
            const userEvents = getEventsForUser(user.id);

            return (
              <div key={user.id} className="relative border-r last:border-r-0">
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className={cn(
                      "absolute left-0 right-0 border-t hover:bg-accent/20 transition-colors cursor-pointer",
                      hour >= WORK_HOURS.start && hour < WORK_HOURS.end ? "border-border/50" : "border-border/20 bg-muted/30"
                    )}
                    style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                    onClick={() => {
                      const clickDate = new Date(date);
                      clickDate.setHours(hour, 0, 0, 0);
                      onTimeSlotClick(clickDate);
                    }}
                  />
                ))}

                {isToday && <CurrentTimeIndicator />}

                {userEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className={cn(
                      "absolute rounded-md px-1.5 py-0.5 cursor-pointer overflow-hidden transition-all",
                      "hover:ring-2 hover:ring-offset-1 hover:ring-primary/50",
                      "shadow-sm border-l-3"
                    )}
                    style={{
                      ...getEventStyle(event),
                      backgroundColor: userColor.lightColor,
                      borderLeftColor: userColor.color,
                      borderLeftWidth: '3px'
                    }}
                  >
                    <div className="text-[10px] font-medium truncate" style={{ color: userColor.color }}>
                      {event.title}
                    </div>
                    {event.location && (
                      <div className="text-[9px] text-muted-foreground truncate">
                        {event.location}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================================
// MONTH VIEW COMPONENT
// ============================================================================
export const MonthView = ({ date, events, onEventClick, onDragStart, onDateClick, onDropEvent }) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const todayStr = new Date().toISOString().split('T')[0];

  const days = [];
  
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const d = new Date(year, month - 1, day);
    days.push({ day, date: d, isCurrentMonth: false });
  }
  
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    days.push({ day, date: d, isCurrentMonth: true });
  }
  
  const remaining = 42 - days.length;
  for (let day = 1; day <= remaining; day++) {
    const d = new Date(year, month + 1, day);
    days.push({ day, date: d, isCurrentMonth: false });
  }

  const getEventsForDate = (d) => {
    const dateStr = d.toISOString().split('T')[0];
    return events.filter(e => {
      const start = new Date(e.start_date).toISOString().split('T')[0];
      const end = new Date(e.end_date).toISOString().split('T')[0];
      return dateStr >= start && dateStr <= end;
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="grid grid-cols-7 border-b w-full">
        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
          <div key={day} className="text-center py-2 text-sm font-medium text-muted-foreground border-l first:border-l-0">
            {day}
          </div>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-7 grid-rows-6 w-full">
        {days.map(({ day, date: d, isCurrentMonth }, i) => {
          const dateStr = d.toISOString().split('T')[0];
          const isToday = dateStr === todayStr;
          const dayEvents = getEventsForDate(d);
          const maxVisible = 3;

          return (
            <div
              key={i}
              className={cn(
                "min-h-[100px] border-t border-l first:border-l-0 p-1 cursor-pointer hover:bg-accent/30 transition-colors",
                !isCurrentMonth && "bg-muted/30",
                isToday && "bg-primary/5"
              )}
              onClick={() => onDateClick(d)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDropEvent(e, d)}
            >
              <div className={cn(
                "text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full",
                isToday && "bg-primary text-primary-foreground",
                !isCurrentMonth && "text-muted-foreground"
              )}>
                {day}
              </div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, maxVisible).map(event => {
                  const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
                  const startTime = new Date(event.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  return (
                    <div
                      key={event.id}
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        onDragStart(e, event);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                      className="text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                      style={{ backgroundColor: eventType.lightColor, color: eventType.color }}
                    >
                      <span className="font-medium">{startTime}</span> {event.title}
                    </div>
                  );
                })}
                {dayEvents.length > maxVisible && (
                  <div className="text-xs text-muted-foreground pl-1.5 font-medium">
                    +{dayEvents.length - maxVisible} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
